import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import type { IRule } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import DurationServiceInstance from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";

import { SourceTypeMap } from "app/common/keyword-research/website-keywords/tableConsts";

import { DEFAULT_PAGE_SIZE } from "./constants";
import { useTopicsEndpoints } from "./topicsEndpoints";
import { TopicsTable } from "./TopicsTable";
import type { ITopicsResponse, SortConfig } from "./types";
import { transformTopicsData } from "./utils";

const EXCLUDE_FILTER_PARSING_ERROR_MESSAGE = "Error making the post request:";

export const initialTableSort: SortConfig = { asc: false, sort: "ClicksShare" };

interface ITopicsTableContainerProps {
    showKeywordGroupButton?: boolean;
}

export const TopicsTableContainer = ({ showKeywordGroupButton = true }: ITopicsTableContainerProps) => {
    const { params } = useCurrentStateAndParams();
    const { country, webSource, duration, key, selectedPageTab, pageFilter, multiIncludeExcludeTopic, Change, trend } =
        params;
    const endpoints = useTopicsEndpoints();

    const durationData = DurationServiceInstance.getDurationData(duration);
    const { from, to, isWindow } = durationData.forAPI;
    const [currentPage, setCurrentPage] = useState(1);

    const requestBody: (IRule & { field: string })[] = useMemo(() => {
        const topics: IRule[] = safeJsonParse(
            multiIncludeExcludeTopic,
            [] as IRule[],
            EXCLUDE_FILTER_PARSING_ERROR_MESSAGE,
        );
        return topics.map((rule) => ({ ...rule, field: "topic" }));
    }, [multiIncludeExcludeTopic]);

    const updateCurrentPage = (page: number) => {
        setCurrentPage(page);
    };

    const [sort, setSort] = useState<SortConfig>({
        sort: initialTableSort.sort,
        asc: initialTableSort.asc,
    });

    const updateSort = (sorter: SortConfig) => {
        setSort(sorter);
    };

    const apiParams = {
        ...sort,
        country,
        webSource,
        to,
        from,
        isWindow,
        keys: key,
        pageFilterJson: pageFilter,
        page: currentPage,
        Change,
        trend,
        sourceType: SourceTypeMap[selectedPageTab],
        pageSize: DEFAULT_PAGE_SIZE,
    };
    const paramsAsString = requestParams(apiParams);
    const excelUrl = `${endpoints.excel}?${paramsAsString}`;

    const { data, isLoading, isError } = useQuery({
        queryKey: [endpoints.table, apiParams, requestBody],
        queryFn: async () =>
            DefaultFetchService.getInstance().post<ITopicsResponse>(
                `${endpoints.table}?${paramsAsString}`,
                requestBody,
            ),
        select: (response: ITopicsResponse) => transformTopicsData(response),
    });

    return (
        <TopicsTable
            data={data?.data ?? []}
            isLoading={isLoading}
            isError={isError}
            sortConfig={sort}
            handleSort={updateSort}
            totalCount={data?.totalCount ?? 0}
            currentPage={currentPage}
            onPageChange={updateCurrentPage}
            excelUrl={excelUrl}
            requestBodyForExcel={requestBody}
            selectedPageTab={selectedPageTab}
            showKeywordGroupButton={showKeywordGroupButton}
        />
    );
};
